/* Paw trail — a tiger walking the margins of the homepage.
 *
 * One continuous walk. A paw lands every STEP_SCROLL px of scrolling, holds
 * solid for a few paws' worth of scroll, then dries off the page behind you,
 * so roughly six prints are on screen at any moment: one arriving, three or
 * four solid, one or two going. There is no run-and-gap any more -- the trail
 * simply keeps walking as long as you keep scrolling.
 *
 * Three things about the model are worth stating, because they are what make
 * it behave the way it does:
 *
 * 1. A paw's position on screen is fixed; what changes with scroll is whether
 *    it has been laid down yet. So the trail advances *downwards* as the page
 *    goes down, rather than riding the document upwards the way normal content
 *    does. Prints are anchored to a range of scroll, not to a place in the page.
 *    One descent walks the screen top to bottom, crossing from the left margin
 *    to the right halfway down without giving up any height -- see SIDES.
 *
 * 2. Everything is a pure function of scrollY. There is no animation playing
 *    and no index being stepped, so scrolling back up is not a rewind that had
 *    to be implemented -- it is the same function read backwards. The
 *    irregularity is hashed from the paw's index for the same reason: it has to
 *    come out identical every time that paw is asked about.
 *
 * 3. Only POOL elements exist. They are recycled as the window of live paws
 *    advances, which is why the walk can run forever without the DOM growing.
 *
 * The layer is position: fixed and never contributes to the document's height
 * or width, which this theme is sensitive to; see the two traps in CLAUDE.md.
 *
 * The envelope below is measured off the brand's own paw animation
 * (Brand Assets/TT Paw Animation Red 2 (1).webm): near-linear ramps in and
 * out around a solid hold, and a stride that zigzags rather than ruling a line.
 * That clip keeps about three prints up at once; this keeps about six, which is
 * what the homepage was asked for.
 */

(() => {
  /* ---- pacing ---------------------------------------------------------- */
  const STEP_SCROLL = 130; // px of scrolling between one paw landing and the next
  const FIRST_PAW_AT = 200; // px scrolled before the first paw lands

  /* ---- the visible window, measured in paws ---------------------------- */
  const FADE_IN = 1.15; // paws' worth of scroll a print takes to come up solid
  const HOLD = 3.0; // how long it then stays solid
  const FADE_OUT = 1.6; // and how long it takes to leave

  /* Phones (whenever CSS sets --paw-soak): a print soaks in and dries off
   * slowly and is settled for less of its life, so the trail is fewer, softer
   * marks -- about five up at once, most of them arriving or leaving. */
  const SOFT_FADE_IN = 1.5;
  const SOFT_HOLD = 1.4;
  const SOFT_FADE_OUT = 2.1;

  /* ---- gait. Every distance is a multiple of --paw-size, so the walk
   *      keeps its proportions when the paw is resized in CSS. ----------- */
  const STRIDE = 2.0; // vertical gap between prints
  const LATERAL = 0.75; // how far a foot sits either side of the centreline
  const DRIFT_X = 0.9; // how far the trail wanders inwards over one pass
  const TOP_FRAC = 0.08; // where a descent starts in the viewport
  const SPAN_FRAC = 0.84; // the most of the viewport one full descent may take

  /* Steps down one margin before the walk crosses to the other. The cap; the
   * actual count is shortened to whatever fits, see measure(). */
  const PASS_PAWS = 10;

  /* The walk descends the screen once, crossing from the left margin to the
   * right halfway down, and only then returns to the top.
   *
   * It used to reset to TOP_FRAC on every crossing: ten steps down the left,
   * then back up to the top and ten down the right. Two consequences, both
   * wrong. The trail read as two separate runs rather than one animal walking,
   * and because a pass ran out while its own marks were still drying, the tail
   * of one was finishing bottom-left while the head of the next was already
   * starting top-right -- two disconnected trails on screen at once.
   *
   * Now `y` is a function of position within the whole left-then-right cycle,
   * so crossing sides costs no height: the tiger keeps going down. The only
   * jump left is one per cycle, from the bottom of the screen back to the top,
   * which reads as a walk leaving the frame rather than a trail restarting
   * halfway up it.
   *
   * The cost is that both margins now share one screen's worth of height
   * instead of each getting its own, so a side gets ~8 steps where it used to
   * get 10. */
  const SIDES = 2;

  /* ---- irregularity. A walk is not a ruler: the reference clip's prints
   *      wander by most of a paw width and land slightly off the beat. --- */
  const JIT_X = 0.35; // × paw size
  const JIT_Y = 0.22; // × stride
  const JIT_TILT = 10; // degrees
  const JIT_SCALE = 0.1;
  const JIT_LAG = 0.28; // paws — how far off the beat a print may land
  const JIT_HOLD = 0.4; // paws — how much longer one print may stay than another

  /* ---- ink. A print is one solid mark now. It used to be two: a blurred bleed
   *      that led the sharp mark in and outlasted it going out, dropping back to
   *      a residue in between so a settled print did not keep a standing halo.
   *      The blur was removed by request, and with it the only thing that ever
   *      scaled -- the sharp mark's size is fixed. On phones a lighter, softer
   *      version is back for the fades only (see --paw-soak in the CSS). ---- */

  /* Elements are recycled, so this only has to exceed the widest the live
   * window can ever get (liveSpan, set in measure(): at most ~7.4 paws) -- not
   * the number of paws in the page. */
  const POOL = 14;

  const PROBE_EVERY = 55; // px of scroll between light-or-dark re-checks

  /* Replaced content, whose brightness the hit-test cannot read. */
  const IMAGERY = /^(IMG|PICTURE|VIDEO|CANVAS|SVG|IFRAME)$/i;

  const PAW_PATH =
    'M39.041,36.843c2.054,3.234,3.022,4.951,3.022,6.742c0,3.537-2.627,5.252-6.166,5.252c-1.56,0-2.567-0.002-5.112-1.326c0,0-1.649-1.509-5.508-1.354c-3.895-0.154-5.545,1.373-5.545,1.373c-2.545,1.323-3.516,1.309-5.074,1.309c-3.539,0-6.168-1.713-6.168-5.252c0-1.791,0.971-3.506,3.024-6.742c0,0,3.881-6.445,7.244-9.477c2.43-2.188,5.973-2.18,5.973-2.18h1.093v-0.001c0,0,3.698-0.009,5.976,2.181C35.059,30.51,39.041,36.844,39.041,36.843z M16.631,20.878c3.7,0,6.699-4.674,6.699-10.439S20.331,0,16.631,0S9.932,4.674,9.932,10.439S12.931,20.878,16.631,20.878z M10.211,30.988c2.727-1.259,3.349-5.723,1.388-9.971s-5.761-6.672-8.488-5.414s-3.348,5.723-1.388,9.971C3.684,29.822,7.484,32.245,10.211,30.988z M32.206,20.878c3.7,0,6.7-4.674,6.7-10.439S35.906,0,32.206,0s-6.699,4.674-6.699,10.439C25.507,16.204,28.506,20.878,32.206,20.878z M45.727,15.602c-2.728-1.259-6.527,1.165-8.488,5.414s-1.339,8.713,1.389,9.972c2.728,1.258,6.527-1.166,8.488-5.414S48.455,16.861,45.727,15.602z';

  const layer = document.querySelector('.paw-trail');
  if (!layer) return;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

  const pool = [];
  let vh = 0;
  let vw = 0;
  let pawSize = 18;
  let stride = 57;
  let passPaws = 12;
  let inset = 36;
  let ticking = false;
  let frame = 0;
  let lastProbeAt = -Infinity;
  let soak = 0; // px of blur on a print that is only just arriving or leaving; 0 = off
  let fadeIn = FADE_IN;
  let hold = HOLD;
  let fadeOut = FADE_OUT;
  let liveSpan = FADE_IN + HOLD + FADE_OUT + JIT_HOLD + JIT_LAG + 1;

  /* 0 before `from`, 1 after `to`, linear between. */
  function ramp(value, from, to) {
    if (to === from) return value >= to ? 1 : 0;
    const t = (value - from) / (to - from);
    return t < 0 ? 0 : t > 1 ? 1 : t;
  }

  /* Stable pseudo-random in -1..1 for a given paw index and channel. Has to be
   * a hash rather than Math.random: the same paw must come out the same way
   * every time it is asked about, including on the way back up and after the
   * element that drew it has been recycled to another index and back. */
  function jitter(index, channel) {
    let h = Math.imul(index ^ 0x9e3779b9, 0x85ebca6b) ^ Math.imul(channel + 1, 0xc2b2ae35);
    h = Math.imul(h ^ (h >>> 15), 0x27d4eb2f);
    h ^= h >>> 15;
    return ((h >>> 0) / 2147483648) - 1;
  }

  function markSvg(className) {
    const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
    svg.setAttribute('class', className);
    svg.setAttribute('viewBox', '0 0 48.839 48.839');
    svg.setAttribute('focusable', 'false');
    svg.setAttribute('aria-hidden', 'true');
    const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
    path.setAttribute('d', PAW_PATH);
    svg.appendChild(path);
    return svg;
  }

  function makePaw() {
    const el = document.createElement('span');
    el.className = 'paw-trail__paw';

    // Carries the print's tilt and mirroring, so the per-frame transform on the
    // paw itself stays a bare translate.
    const stamp = document.createElement('span');
    stamp.className = 'paw-trail__stamp';

    const ink = markSvg('paw-trail__mark paw-trail__mark--ink');
    stamp.appendChild(ink);
    el.appendChild(stamp);
    layer.appendChild(el);

    return {
      el: el,
      stamp: stamp,
      ink: ink,
      index: null,
      frame: -1,
      lag: 0,
      hold: HOLD,
      x: 0,
      y: 0,
      dark: null,
      shownInk: -1,
      shownBlur: -1
    };
  }

  /* How far in from the edge the trail walks: a comfortable margin on a
   * desktop, and never less than the room a print needs to land fully on screen
   * at the outer end of its lateral swing plus its jitter. Half a paw would
   * technically clear the edge (0.5); 0.85 leaves the outermost print a visible
   * gap rather than having it graze the screen edge on a phone. Only binds
   * below the breakpoint -- on a desktop 4.5% of the width is always wider. */
  function sideInset() {
    const min = pawSize * (LATERAL + JIT_X + 0.85);
    if (window.innerWidth < 900) return min;
    return Math.min(Math.max(min, window.innerWidth * 0.045), 104);
  }

  function measure() {
    vh = window.innerHeight;
    // clientWidth, not innerWidth: the layer is inset: 0 on a fixed element, so
    // it stops at the scrollbar. innerWidth would push right-hand prints under it.
    vw = document.documentElement.clientWidth;
    // CSS owns the paw's size, including its breakpoint; read it rather than
    // keeping a second copy here that can drift out of step.
    const cs = getComputedStyle(layer);
    pawSize = parseFloat(cs.getPropertyValue('--paw-size')) || 18;
    soak = parseFloat(cs.getPropertyValue('--paw-soak')) || 0;
    fadeIn = soak ? SOFT_FADE_IN : FADE_IN;
    hold = soak ? SOFT_HOLD : HOLD;
    fadeOut = soak ? SOFT_FADE_OUT : FADE_OUT;
    // Elements are recycled, so the pool only has to exceed the widest the live
    // window can get: a print's life plus the most it can lag or linger.
    liveSpan = fadeIn + hold + fadeOut + JIT_HOLD + JIT_LAG + 1;
    stride = pawSize * STRIDE;
    // One descent is SIDES passes end to end, so it is the *cycle* that has to
    // fit the span, not a single pass. Shortened where the viewport is too
    // shallow to hold the lot.
    const cycleSteps = Math.floor((vh * SPAN_FRAC) / stride) + 1;
    passPaws = Math.max(3, Math.min(PASS_PAWS, Math.floor(cycleSteps / SIDES)));
    inset = sideInset();

    for (const paw of pool) {
      paw.index = null; // force a re-place at the new size
      paw.shownBlur = -1;
      paw.ink.style.filter = ''; // back to the stylesheet's until the next write
    }
  }

  /* Where paw `k` lands, and how it is turned. Pure function of k and the
   * current viewport. */
  function place(paw, k) {
    const cyclePaws = passPaws * SIDES;
    // Where this print falls in the whole descent, and which margin that puts
    // it in. `descent` drives the height, `pos` only the sideways drift -- that
    // is the whole of the change: crossing sides no longer rewinds the height.
    const descent = ((k % cyclePaws) + cyclePaws) % cyclePaws;
    const left = descent < passPaws;
    const pos = descent - (left ? 0 : passPaws);
    const foot = k % 2 === 0 ? -1 : 1;

    const out =
      inset +
      foot * LATERAL * pawSize +
      (pos / Math.max(1, passPaws - 1)) * DRIFT_X * pawSize +
      jitter(k, 1) * JIT_X * pawSize;

    paw.x = Math.round(left ? out : vw - out);
    paw.y = Math.round(vh * TOP_FRAC + descent * stride + jitter(k, 2) * JIT_Y * stride);
    paw.el.style.transform = 'translate3d(' + paw.x + 'px, ' + paw.y + 'px, 0)';

    // The glyph is drawn toes-up, so a print laid down as-is faces back up the
    // page while the trail advances down it -- the tiger walking backwards.
    // The 180 turns it to face the direction of travel. Note the mirroring
    // still works through it: rotate(180) scale(-1, 1) is a vertical flip, so
    // both feet come out pointing down and mirrored about the centreline, the
    // way a real pair of prints sits on the ground.
    const tilt = 180 + foot * 8 + (left ? 5 : -5) + jitter(k, 3) * JIT_TILT;
    const size = 1 + jitter(k, 4) * JIT_SCALE;
    paw.stamp.style.transform =
      'rotate(' + tilt.toFixed(1) + 'deg) scale(' + (foot * size).toFixed(3) + ',' + size.toFixed(3) + ')';

    paw.lag = jitter(k, 5) * JIT_LAG;
    paw.hold = hold + jitter(k, 6) * JIT_HOLD;
  }

  /* The element currently standing in for paw `k`. Because the live window is
   * always narrower than POOL, no two live paws ever want the same one. */
  function claim(k) {
    const paw = pool[((k % POOL) + POOL) % POOL];
    if (paw.index === k) return paw;

    paw.index = k;
    place(paw, k);
    hide(paw);
    paw.dark = null; // new spot, so re-ask what is behind it
    probe(paw);
    return paw;
  }

  function update(force) {
    const scrollY = window.scrollY || window.pageYOffset;
    const head = (scrollY - FIRST_PAW_AT) / STEP_SCROLL;
    const probing = force || Math.abs(scrollY - lastProbeAt) > PROBE_EVERY;
    if (probing) lastProbeAt = scrollY;

    frame++;

    const lo = Math.max(0, Math.floor(head - liveSpan));
    const hi = Math.ceil(head);

    for (let k = lo; k <= hi; k++) {
      const paw = claim(k);
      paw.frame = frame;

      const age = head - k - paw.lag;
      const dries = fadeIn + paw.hold;

      // The mark comes up, holds, then goes. On a desktop the ramps are almost
      // linear; on a phone they ease at both ends, so a print seeps in and
      // dries away instead of arriving at a steady rate.
      const inkOn = ramp(age, 0, fadeIn);
      const inkOff = ramp(age, dries, dries + fadeOut);
      const inkAlpha = soak
        ? smooth(inkOn) * (1 - smooth(inkOff))
        : Math.pow(inkOn, 1.15) * (1 - Math.pow(inkOff, 0.9));

      write(paw, inkAlpha);

      if (probing && inkAlpha > 0.02) probe(paw);
    }

    // Whatever the window has walked past.
    for (const paw of pool) {
      if (paw.frame !== frame) hide(paw);
    }
  }

  function write(paw, inkAlpha) {
    const ink = Math.round(inkAlpha * 100) / 100;
    if (ink !== paw.shownInk) {
      paw.ink.style.opacity = String(ink);
      paw.shownInk = ink;
    }
    // Soft at the edges of its life, sharp while settled: ink spreading into
    // the paper on the way in, and drying out of it on the way out.
    if (soak) {
      const blur = Math.round(soak * (1 - ink) * 10) / 10;
      if (blur !== paw.shownBlur) {
        paw.ink.style.filter = blur > 0 ? 'blur(' + blur + 'px)' : 'none';
        paw.shownBlur = blur;
      }
    }
  }

  /* Eases in and out of 0..1. */
  function smooth(t) {
    return t * t * (3 - 2 * t);
  }

  function hide(paw) {
    if (paw.shownInk !== 0) {
      paw.ink.style.opacity = '0';
      paw.shownInk = 0;
    }
  }

  /* Light or dark behind this paw?
   *
   * The prints are solid now, so getting this wrong is not a subtle loss of
   * contrast -- a maroon print on the maroon Hoysala band would simply not be
   * there. The paw holds its place on screen while the page slides past
   * underneath, so unlike a document-anchored mark this has to be re-asked as
   * you scroll rather than settled once: on every recycle, and otherwise every
   * PROBE_EVERY px for paws that are actually showing.
   *
   * Sections here paint their background at wildly different depths -- the
   * theme's own use a .section-background child, the ai_gen_* blocks go several
   * levels down -- so rather than guess at the structure, hit-test the point
   * and walk up from whatever is really there. The layer is pointer-events:
   * none, so it never finds itself.
   */
  function probe(paw) {
    const dark = isOnDark(paw.x, paw.y);
    if (dark === paw.dark) return;
    paw.dark = dark;
    paw.el.classList.toggle('paw-trail__paw--on-dark', dark);
  }

  function isOnDark(x, y) {
    if (y < 0 || y > vh) return false;
    let el = document.elementFromPoint(x, y);
    while (el && el !== document.documentElement) {
      // Stop at a picture rather than walking past it. Its own brightness is
      // unreadable from here -- it is a cross-origin CDN image, so a canvas
      // sample would be tainted -- but inheriting the *section's* is worse than
      // guessing: the Hoysala band is maroon and the parchment plate inside it
      // is nearly cream, so walking up put a cream print on a cream
      // illustration. This theme's imagery is overwhelmingly light (parchment
      // plates, product shots on cream), so guess light and let the ink's
      // counter-tone hairline carry the few that are not.
      if (IMAGERY.test(el.tagName)) return false;
      const cs = getComputedStyle(el);
      if (cs.backgroundImage !== 'none') return false;
      const lum = luminanceOf(cs.backgroundColor);
      if (lum !== null) return lum < 0.42;
      el = el.parentElement;
    }
    return false;
  }

  /* Relative luminance, or null for anything see-through enough not to count. */
  function luminanceOf(color) {
    const m = /^rgba?\(([\d.]+),\s*([\d.]+),\s*([\d.]+)(?:,\s*([\d.]+))?\)$/.exec(color || '');
    if (!m) return null;
    if (m[4] !== undefined && parseFloat(m[4]) < 0.85) return null;
    return 0.2126 * (+m[1] / 255) + 0.7152 * (+m[2] / 255) + 0.0722 * (+m[3] / 255);
  }

  function onScroll() {
    if (ticking) return;
    ticking = true;
    requestAnimationFrame(() => {
      ticking = false;
      update(false);
    });
  }

  let remeasureTimer = 0;
  function scheduleRemeasure() {
    clearTimeout(remeasureTimer);
    remeasureTimer = setTimeout(() => {
      measure();
      update(true);
    }, 180);
  }

  for (let i = 0; i < POOL; i++) pool.push(makePaw());

  window.addEventListener('scroll', onScroll, { passive: true });
  window.addEventListener('resize', scheduleRemeasure);
  window.addEventListener('orientationchange', scheduleRemeasure);

  measure();
  update(true);
})();
